export interface CspSafeAudioPlayerCallbacks {
  onTimeUpdate?: (currentTime: number) => void;
  onDuration?: (duration: number) => void;
  onEnded?: () => void;
  onError?: (message: string) => void;
  onPlayingChange?: (playing: boolean) => void;
}

type PlaybackMode = 'webaudio' | 'htmlaudio';

/**
 * Plays remote recordings. Tries fetch + Web Audio first (for strict CSP media-src).
 * Falls back to HTMLAudioElement when fetch fails (e.g. CloudFront without CORS headers).
 */
export class CspSafeAudioPlayer {
  private context: AudioContext | null = null;
  private buffer: AudioBuffer | null = null;
  private source: AudioBufferSourceNode | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private mode: PlaybackMode | null = null;
  private loadGeneration = 0;
  private loadedUrl: string | null = null;
  private startedAt = 0;
  private offsetAtStart = 0;
  private playing = false;
  private tickId: number | null = null;
  private intentionalStop = false;
  private playbackRate = 1;

  private readonly htmlAudioTimeUpdate = () => {
    if (this.audioElement && this.callbacks.onTimeUpdate) {
      this.callbacks.onTimeUpdate(this.audioElement.currentTime);
    }
  };

  private readonly htmlAudioEnded = () => {
    this.playing = false;
    if (this.callbacks.onPlayingChange) {
      this.callbacks.onPlayingChange(false);
    }
    if (this.callbacks.onEnded) {
      this.callbacks.onEnded();
    }
  };

  constructor(private callbacks: CspSafeAudioPlayerCallbacks = {}) {}

  get paused(): boolean {
    return !this.playing;
  }

  get currentTime(): number {
    if (this.mode === 'htmlaudio' && this.audioElement) {
      return this.audioElement.currentTime;
    }
    if (this.playing && this.context) {
      return Math.min(
        this.offsetAtStart + (this.context.currentTime - this.startedAt) * this.playbackRate,
        this.duration
      );
    }
    return this.offsetAtStart;
  }

  set currentTime(value: number) {
    this.seek(value);
  }

  get duration(): number {
    if (this.mode === 'htmlaudio' && this.audioElement && isFinite(this.audioElement.duration)) {
      return this.audioElement.duration;
    }
    return this.buffer ? this.buffer.duration : 0;
  }

  get url(): string | null {
    return this.loadedUrl;
  }

  isLoaded(url: string): boolean {
    if (this.loadedUrl !== url) {
      return false;
    }
    if (this.mode === 'webaudio') {
      return !!this.buffer;
    }
    if (this.mode === 'htmlaudio') {
      return !!this.audioElement && this.audioElement.readyState >= HTMLMediaElement.HAVE_METADATA;
    }
    return false;
  }

  async load(url: string): Promise<void> {
    const generation = ++this.loadGeneration;
    this.stopPlayback();
    this.destroyHtmlAudio();
    this.buffer = null;
    this.mode = null;
    this.loadedUrl = null;

    let webAudioError: unknown;
    try {
      await this.loadViaWebAudio(url, generation);
      this.mode = 'webaudio';
      return;
    } catch (err) {
      webAudioError = err;
      if (generation !== this.loadGeneration) {
        return;
      }
    }

    // HTMLAudio fallback when fetch/CORS fails. Note: this only works if CSP
    // allows the host via media-src (or default-src). Our app CSP uses
    // default-src 'self' with no media-src, so external <audio src> is blocked —
    // Web Audio + CORS is the supported path for remote recordings.
    try {
      await this.loadViaHtmlAudio(url, generation);
      this.mode = 'htmlaudio';
    } catch (htmlError) {
      throw buildLoadFailureError(url, webAudioError, htmlError);
    }
  }

  async playUrl(url: string): Promise<void> {
    if (this.isLoaded(url)) {
      this.play();
      return;
    }
    await this.load(url);
    this.play();
  }

  play(): void {
    if (this.mode === 'htmlaudio' && this.audioElement) {
      if (this.playing) {
        return;
      }
      this.audioElement.play()
        .then(() => {
          this.playing = true;
          if (this.callbacks.onPlayingChange) {
            this.callbacks.onPlayingChange(true);
          }
        })
        .catch((err) => {
          if (this.callbacks.onError) {
            this.callbacks.onError(getFetchAudioErrorMessage(err));
          }
        });
      return;
    }

    if (!this.buffer || !this.context || this.playing) {
      return;
    }
    this.startWebAudioSource(this.offsetAtStart);
  }

  pause(): void {
    if (this.mode === 'htmlaudio' && this.audioElement) {
      if (!this.playing) {
        return;
      }
      this.audioElement.pause();
      this.playing = false;
      if (this.callbacks.onPlayingChange) {
        this.callbacks.onPlayingChange(false);
      }
      return;
    }

    if (!this.playing || !this.context) {
      return;
    }
    this.offsetAtStart = this.currentTime;
    this.stopWebAudioSource();
  }

  setPlaybackRate(rate: number): void {
    const nextRate = Math.max(0.25, Math.min(rate, 4));
    if (this.mode === 'webaudio' && this.playing && this.context) {
      this.offsetAtStart = this.currentTime;
      this.startedAt = this.context.currentTime;
    }
    this.playbackRate = nextRate;
    if (this.mode === 'htmlaudio' && this.audioElement) {
      this.audioElement.playbackRate = nextRate;
    }
    if (this.source) {
      this.source.playbackRate.value = nextRate;
    }
  }

  seek(time: number): void {
    const maxDuration = this.duration;
    const clamped = Math.max(0, Math.min(time, maxDuration || 0));

    if (this.mode === 'htmlaudio' && this.audioElement) {
      this.audioElement.currentTime = clamped;
      if (this.callbacks.onTimeUpdate) {
        this.callbacks.onTimeUpdate(clamped);
      }
      return;
    }

    const wasPlaying = this.playing;
    if (this.playing) {
      this.stopWebAudioSource();
    }
    this.offsetAtStart = clamped;
    if (this.callbacks.onTimeUpdate) {
      this.callbacks.onTimeUpdate(clamped);
    }
    if (wasPlaying) {
      this.startWebAudioSource(clamped);
    }
  }

  toggle(url: string): Promise<void> {
    if (this.isLoaded(url)) {
      if (this.playing) {
        this.pause();
        return Promise.resolve();
      }
      this.play();
      return Promise.resolve();
    }
    return this.playUrl(url);
  }

  destroy(): void {
    this.loadGeneration++;
    this.stopPlayback();
    this.destroyHtmlAudio();
    if (this.context) {
      this.context.close().catch(() => undefined);
      this.context = null;
    }
    this.buffer = null;
    this.loadedUrl = null;
    this.mode = null;
    this.offsetAtStart = 0;
    this.playing = false;
  }

  private async loadViaWebAudio(url: string, generation: number): Promise<void> {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!response.ok) {
      throw new Error('Network error while loading recording');
    }

    const arrayBuffer = await response.arrayBuffer();
    if (generation !== this.loadGeneration) {
      return;
    }

    const win = window as any;
    const AudioCtx = win.AudioContext || win.webkitAudioContext;
    if (!this.context) {
      this.context = new AudioCtx();
    }
    if (this.context.state === 'suspended') {
      await this.context.resume();
    }

    try {
      this.buffer = await this.context.decodeAudioData(arrayBuffer.slice(0));
    } catch (e) {
      throw new Error(
        'Unable to decode recording. The file may use the wrong format or Content-Type on the server.'
      );
    }

    if (generation !== this.loadGeneration) {
      return;
    }

    this.loadedUrl = url;
    this.offsetAtStart = 0;
    if (this.callbacks.onDuration) {
      this.callbacks.onDuration(this.buffer.duration);
    }
  }

  private loadViaHtmlAudio(url: string, generation: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.destroyHtmlAudio();

      const audio = new Audio();
      audio.preload = 'auto';
      audio.playbackRate = this.playbackRate;

      const handleReady = () => {
        if (generation !== this.loadGeneration) {
          return;
        }
        if (!isFinite(audio.duration) || audio.duration <= 0) {
          return;
        }
        cleanup();
        this.audioElement = audio;
        this.loadedUrl = url;
        this.offsetAtStart = 0;
        this.bindHtmlAudioEvents(audio);
        if (this.callbacks.onDuration) {
          this.callbacks.onDuration(audio.duration);
        }
        resolve();
      };

      const handleError = () => {
        if (generation !== this.loadGeneration) {
          return;
        }
        cleanup();
        const mediaError = audio.error;
        const detail = mediaError
          ? ` (media error code ${mediaError.code})`
          : '';
        reject(new Error(`Unable to load recording${detail}`));
      };

      const cleanup = () => {
        audio.removeEventListener('loadedmetadata', handleReady);
        audio.removeEventListener('durationchange', handleReady);
        audio.removeEventListener('error', handleError);
      };

      audio.addEventListener('loadedmetadata', handleReady);
      audio.addEventListener('durationchange', handleReady);
      audio.addEventListener('error', handleError);
      audio.src = url;
      audio.load();
    });
  }

  private bindHtmlAudioEvents(audio: HTMLAudioElement): void {
    audio.addEventListener('timeupdate', this.htmlAudioTimeUpdate);
    audio.addEventListener('ended', this.htmlAudioEnded);
  }

  private unbindHtmlAudioEvents(audio: HTMLAudioElement): void {
    audio.removeEventListener('timeupdate', this.htmlAudioTimeUpdate);
    audio.removeEventListener('ended', this.htmlAudioEnded);
  }

  private destroyHtmlAudio(): void {
    if (!this.audioElement) {
      return;
    }
    this.unbindHtmlAudioEvents(this.audioElement);
    this.audioElement.pause();
    this.audioElement.removeAttribute('src');
    this.audioElement.load();
    this.audioElement = null;
  }

  private stopPlayback(): void {
    this.stopWebAudioSource();
    if (this.audioElement) {
      this.audioElement.pause();
    }
    this.playing = false;
  }

  private startWebAudioSource(offset: number): void {
    if (!this.buffer || !this.context) {
      return;
    }
    this.stopWebAudioSource();

    this.source = this.context.createBufferSource();
    this.source.buffer = this.buffer;
    this.source.playbackRate.value = this.playbackRate;
    this.source.connect(this.context.destination);
    this.source.onended = () => {
      if (this.intentionalStop) {
        this.intentionalStop = false;
        return;
      }
      if (!this.playing) {
        return;
      }
      this.offsetAtStart = 0;
      this.stopWebAudioSource();
      if (this.callbacks.onEnded) {
        this.callbacks.onEnded();
      }
    };

    this.source.start(0, offset);
    this.startedAt = this.context.currentTime;
    this.offsetAtStart = offset;
    this.playing = true;
    if (this.callbacks.onPlayingChange) {
      this.callbacks.onPlayingChange(true);
    }
    this.startWebAudioTick();
  }

  private stopWebAudioSource(): void {
    if (this.tickId !== null) {
      cancelAnimationFrame(this.tickId);
      this.tickId = null;
    }
    if (this.source) {
      this.intentionalStop = true;
      try {
        this.source.onended = null;
        this.source.stop();
      } catch (e) {
        // Source may already be stopped.
      }
      this.source.disconnect();
      this.source = null;
    }
    if (this.playing && this.mode === 'webaudio') {
      this.playing = false;
      if (this.callbacks.onPlayingChange) {
        this.callbacks.onPlayingChange(false);
      }
    }
  }

  private startWebAudioTick(): void {
    const tick = () => {
      if (!this.playing) {
        return;
      }
      if (this.callbacks.onTimeUpdate) {
        this.callbacks.onTimeUpdate(this.currentTime);
      }
      this.tickId = requestAnimationFrame(tick);
    };
    this.tickId = requestAnimationFrame(tick);
  }
}

export function getFetchAudioErrorMessage(error: any): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Unable to load recording';
}

function buildLoadFailureError(
  url: string,
  webAudioError: unknown,
  htmlError: unknown
): Error {
  const webMsg = webAudioError instanceof Error ? webAudioError.message : String(webAudioError || '');
  const htmlMsg = htmlError instanceof Error ? htmlError.message : String(htmlError || '');
  const looksLikeCors =
    /Failed to fetch|NetworkError|CORS|cross-origin|Network error/i.test(webMsg) ||
    webAudioError instanceof TypeError;

  if (looksLikeCors) {
    return new Error(
      'Unable to load recording. The host is missing CORS headers (required under this app CSP), so neither Web Audio nor HTML audio can play it.'
    );
  }

  if (webMsg && webMsg !== 'Unable to load recording') {
    return new Error(webMsg);
  }
  if (htmlMsg) {
    return new Error(htmlMsg);
  }
  return new Error(`Unable to load recording: ${url}`);
}

export function getRecordingFileName(url: string): string {
  if (!url) {
    return 'recording.mp3';
  }
  try {
    const pathname = new URL(url).pathname;
    const fileName = pathname.split('/').pop();
    return fileName || 'recording.mp3';
  } catch {
    return 'recording.mp3';
  }
}

export function downloadRecordingFile(url: string): void {
  if (!url) {
    return;
  }

  const fileName = getRecordingFileName(url);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
